import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import Button from "../components/common/Button";
import FormInput from "../components/common/FormInput";
import { useLoginAdminMutation } from "../services/auth/authApi";
import {
  setAdminProfile,
} from "../services/auth/authSession";
import "@fontsource/work-sans/500.css";
import "@fontsource/work-sans/600.css";
import "@fontsource/work-sans/400.css";

const Login = () => {
  const navigate = useNavigate();
  const [loginAdmin, { isLoading }] = useLoginAdminMutation();
  const [apiError, setApiError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: "onSubmit",
  });

  const onSubmit = async (data) => {
    try {
      setApiError("");
      const response = await loginAdmin(data).unwrap();

      if (response?.success === false) {
        setApiError(response?.message || "Login failed. Please check your credentials.");
        return;
      }

      const profile = setAdminProfile(response);

      if (!profile?.email && !profile?.role) {
        setApiError("Login succeeded, but the account details were missing.");
        return;
      }

      navigate("/admin/dashboard", { replace: true });
    } catch (error) {
      setApiError(
        error?.data?.message || "Login failed. Please check your credentials.",
      );
    }
  };

  const isLoggingIn = isSubmitting || isLoading;

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#EFEFFD] px-6 py-10">
      <div className="w-full max-w-[420px] rounded-[17px] bg-[#F5F5F5] px-8 py-12 shadow-[0_2px_8px_0_rgba(0,0,0,0.12)] sm:px-[50px] sm:py-14">
        <h1 className="text-center font-['Work_Sans'] text-2xl font-semibold leading-9 tracking-[-0.02em] text-black">
          <span className="block">Log in to your account</span>
          <span className="block">to Get Started</span>
        </h1>

        <form className="mt-8" noValidate onSubmit={handleSubmit(onSubmit)}>
          <FormInput
            id="email"
            label="Email*"
            type="email"
            placeholder="Registered email"
            labelClassName="block font-['Work_Sans'] text-lg leading-[21px] tracking-[-2%] text-black"
            inputClassName="mt-1.5 h-12 rounded-lg  border-[0.5px] border-[#B9B3C2] px-4  font-medium text-black placeholder:text-[#8C8CA1] focus:border-[#923D8C]"
            errorMessage={errors.email?.message}
            {...register("email", {
              required: "Email is required",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Please enter a valid email address",
              },
            })}
          />

          <FormInput
            className="mt-5"
            id="password"
            label="Password*"
            type={showPassword ? "text" : "password"}
            placeholder="Enter password"
            labelClassName="block font-['Work_Sans'] text-lg leading-[21px] tracking-[-2%] text-black"
            inputClassName="mt-1.5 h-12 rounded-lg border-[0.5px] border-[#B9B3C2] px-4 pr-12 font-medium text-black placeholder:text-[#8C8CA1] focus:border-[#923D8C]"
            errorMessage={errors.password?.message}
            rightContent={
              <button
                type="button"
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="text-[#8C8CA1] transition hover:text-[#923D8C]"
                onClick={() => setShowPassword((current) => !current)}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            }
            {...register("password", {
              required: "Password is required",
            })}
          />

          <Button
            type="submit"
            disabled={isLoggingIn}
            btnText={
              isLoggingIn ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  Logging in...
                </span>
              ) : (
                "Login"
              )
            }
            btnClassName="mt-8 h-12 w-full rounded-full bg-[#923D8C] font-['Work_Sans'] font-semibold leading-[19px] tracking-[0.04em] text-white shadow-[0_6px_2px_-4px_#0E0E2C1A] disabled:cursor-not-allowed disabled:opacity-80"
          />

          {apiError ? (
            <p className="mt-4 text-center text-sm font-medium text-[#C62828]">
              {apiError}
            </p>
          ) : null}
        </form>
      </div>
    </div>
  );
};

export default Login;
